// src/components/QuestionInput.tsx

import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Stack, TextField } from "@fluentui/react";
import { Button } from "@fluentui/react-components";
import styles from "./QuestionInput.module.css";
import sendIcon from "../../assets/sendicon.png";
import { useLogin, requireAccessControl } from "../../authConfig";
import { trackSearch } from "../../analytics";
import { useTranslation } from "../../i18n/useTranslation";

interface Props {
  onSend: (question: string) => void;
  disabled: boolean;
  initQuestion?: string;
  placeholder?: string;
  clearOnSend?: boolean;
}

export const QuestionInput = ({
  onSend,
  disabled,
  placeholder: initialPlaceholder = "Type your question...",
  clearOnSend,
  initQuestion
}: Props) => {
  const [question, setQuestion] = useState<string>("");
  const { instance } = useMsal();
  const { lang } = useTranslation();

  // Prevent double-send in StrictMode / rerenders
  const lastAutoSentInitQuestionRef = useRef<string | null>(null);

  // Are we currently authenticated?
  const isAuthenticated = !useLogin || instance.getActiveAccount() != null;

  // If we require access control and the user isnt signed in, disable input
  const disableRequiredAccessControl = requireAccessControl && !isAuthenticated;

  // Placeholder text can change if we require login
  const placeholder = disableRequiredAccessControl
    ? "Please login to continue…"
    : initialPlaceholder;

  const fireAnalyticsAndSend = (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;

    const opts = {
      pageName: `${lang}:lifenet:ai-search`,
      title: document.title,
      url: window.location.origin + window.location.pathname,
      lang,
      domain: window.location.host,
      fullUrl: `${window.location.href}`
    };

    trackSearch(trimmed, opts);
    onSend(trimmed);

    if (clearOnSend) {
      setQuestion("");
    }
  };

  // Auto-send initQuestion immediately (instead of populating input)
  useEffect(() => {
    const q = initQuestion?.trim();
    if (!q) return;

    // If login is required and user isn't authenticated, don't auto-send
    if (disableRequiredAccessControl) return;

    // Avoid sending same initQuestion multiple times
    if (lastAutoSentInitQuestionRef.current === q) return;
    lastAutoSentInitQuestionRef.current = q;

    fireAnalyticsAndSend(q);
    // keep input empty; no setQuestion(q)
  }, [initQuestion, disableRequiredAccessControl]);

  // Build the final 'disabled' for the send button (manual sending)
  const sendDisabled =
    disabled || !question.trim() || disableRequiredAccessControl;

  const onQuestionChange = (
    _ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) => {
    if (!newValue) {
      setQuestion("");
    } else if (newValue.length <= 1000) {
      setQuestion(newValue);
    }
  };

  const sendQuestion = () => {
    if (sendDisabled) return;
    fireAnalyticsAndSend(question);
  };

  const onEnterPress = (ev: React.KeyboardEvent) => {
    if (ev.key === "Enter" && !ev.shiftKey) {
      ev.preventDefault();
      sendQuestion();
    }
  };

  return (
    <Stack horizontal className={styles.questionInputContainer}>
      <TextField
        className={styles.questionInputTextArea}
        disabled={disableRequiredAccessControl}
        placeholder={placeholder}
        multiline
        resizable={false}
        borderless
        value={question}
        onChange={onQuestionChange}
        onKeyDown={onEnterPress}
      />
      <div className={styles.questionInputButtonsContainer}>
        <Button
          size="large"
          icon={
            <img
              src={sendIcon}
              alt="Send"
              className={styles.sendIcon}
            />
          }
          disabled={sendDisabled}
          onClick={sendQuestion}
        />
      </div>
    </Stack>
  );
};
